import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { flashcardService, isWritingV2Flashcard } from "@/services/flashcardService";
import { frenchVerbFlashcards } from "@/data/frenchVerbFlashcards";
import { frenchConnectorFlashcards } from "@/data/frenchConnectorFlashcards";
import { frenchPrepositionFlashcards } from "@/data/frenchPrepositionFlashcards";
import { Button } from "@/components/ui/button";
import { Brain, ArrowLeft, Loader2, RefreshCw, CheckCircle2, Pencil, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { useStudyTimeTracker } from "@/hooks/useStudyTimeTracker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";


export default function FlashcardStudy() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "due"; // 'due' | 'all'
  const typeParam = searchParams.get("type") || "words"; // 'all' | 'words' | 'verbs' | 'connectors' | 'prepositions'

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);
  const [dueCards, setDueCards] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);

  // Edit flashcard state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editFront, setEditFront] = useState("");
  const [editBack, setEditBack] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Resposta livre (para palavras/frases, conectores e preposições)
  const [typedAnswer, setTypedAnswer] = useState("");

  // Resposta segmentada por pronomes (exclusivo para verbos)
  const [verbAnswers, setVerbAnswers] = useState<{ [key: string]: string }>({
    je: "",
    tu: "",
    ilElleOn: "",
    nous: "",
    vous: "",
    ilsElles: ""
  });

  const getPronounLabel = (key: string): string => {
    const labels: Record<string, string> = {
      je: "Je",
      tu: "Tu",
      ilElleOn: "Il/Elle/On",
      "il/elle/on": "Il/Elle/On",
      nous: "Nous",
      vous: "Vous",
      ilsElles: "Ils/Elles",
      "ils/elles": "Ils/Elles",
    };
    return labels[key] ?? key;
  };

  const answerInputRef = useRef<HTMLTextAreaElement>(null);
  const verbFirstInputRef = useRef<HTMLInputElement>(null);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [reviewedCount, setReviewedCount] = useState(0);

  useStudyTimeTracker({
    activityType: "flashcards",
    competency: "general",
    section: typeParam || "all",
    enabled: !loading && !sessionCompleted && dueCards.length > 0
  });


  useEffect(() => {
    if (!showAnswer) {
      setTimeout(() => {
        if (typeParam === "verbs") {
          verbFirstInputRef.current?.focus();
        } else {
          answerInputRef.current?.focus();
        }
      }, 50);
    }
  }, [currentIndex, showAnswer, typeParam]);

  const fetchDueCards = async () => {
    try {
      setLoading(true);
      let cards: any[] = [];

      if (mode === "all") {
        cards = await flashcardService.getFlashcards({ studyType: typeParam });
      } else {
        cards = await flashcardService.getDueFlashcards({ studyType: typeParam });
      }

      setDueCards(cards);
      setCurrentIndex(0);
      setReviewedCount(0);
      setSessionCompleted(cards.length === 0);
      setShowAnswer(false);
      setTypedAnswer("");
      setVerbAnswers({ je: "", tu: "", ilElleOn: "", nous: "", vous: "", ilsElles: "" });
    } catch (err) {
      console.error("[FlashcardStudy] Error fetching due cards", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDueCards();
  }, [mode, typeParam]);

  const handleRating = async (rating: "again" | "hard" | "good" | "easy") => {
    if (saving) return;
    try {
      setSaving(true);
      const currentCard = dueCards[currentIndex];

      if (currentCard && currentCard.id) {
        await flashcardService.reviewFlashcard(currentCard.id, rating);
      }
      
      // Re-queue card if marked as 'again' so it reappears in the same session
      if (rating === "again") {
        setDueCards(prev => [...prev, currentCard]);
      }
      
      setReviewedCount(prev => prev + 1);
      
      // If we just appended the card, dueCards.length was updated. 
      // But we are using the closure's dueCards.length here, which doesn't have the new card yet.
      // So we check against `dueCards.length + (rating === "again" ? 1 : 0)`.
      if (currentIndex + 1 < dueCards.length + (rating === "again" ? 1 : 0)) {
        setCurrentIndex(prev => prev + 1);
        setShowAnswer(false);
        setTypedAnswer("");
        setVerbAnswers({ je: "", tu: "", ilElleOn: "", nous: "", vous: "", ilsElles: "" });
      } else {
        setSessionCompleted(true);
      }
    } catch (err) {
      console.error("[FlashcardStudy] review failed", err);
      alert(t("flashcardStudy.saveReviewError"));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = () => {
    const currentCard = dueCards[currentIndex];
    if (!currentCard) return;
    setEditFront(currentCard.front || "");
    setEditBack(currentCard.back || "");
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    const currentCard = dueCards[currentIndex];
    if (!currentCard) return;
    if (!editFront.trim()) {
      alert("A frente do card não pode estar vazia.");
      return;
    }
    if (!editBack.trim()) {
      alert("O verso do card não pode estar vazio.");
      return;
    }
    
    try {
      setIsSavingEdit(true);
      const updated = await flashcardService.updateFlashcard(currentCard.id, {
        front: editFront.trim(),
        back: editBack.trim()
      });
      
      setDueCards(prev => prev.map((c, idx) => idx === currentIndex ? { ...c, ...updated } : c));
      setIsEditOpen(false);
    } catch (err) {
      console.error("[FlashcardStudy] Error saving edit", err);
      alert("Erro ao salvar as edições do flashcard.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteCard = async () => {
    const currentCard = dueCards[currentIndex];
    if (!currentCard) return;
    
    const confirmDelete = window.confirm("Tem certeza que deseja excluir permanentemente este flashcard?");
    if (!confirmDelete) return;
    
    try {
      setSaving(true);
      await flashcardService.deleteFlashcard(currentCard.id);
      
      const updatedCards = dueCards.filter((_, idx) => idx !== currentIndex);
      setDueCards(updatedCards);
      
      if (updatedCards.length === 0) {
        setSessionCompleted(true);
      } else if (currentIndex >= updatedCards.length) {
        setCurrentIndex(updatedCards.length - 1);
        setShowAnswer(false);
        setTypedAnswer("");
        setVerbAnswers({ je: "", tu: "", ilElleOn: "", nous: "", vous: "", ilsElles: "" });
      } else {
        setShowAnswer(false);
        setTypedAnswer("");
        setVerbAnswers({ je: "", tu: "", ilElleOn: "", nous: "", vous: "", ilsElles: "" });
      }
    } catch (err) {
      console.error("[FlashcardStudy] Error deleting card", err);
      alert("Erro ao excluir o flashcard.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4 text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="font-medium animate-pulse">{t("flashcardStudy.loading")}</p>
        </div>
      </div>
    );
  }

  if (sessionCompleted || dueCards.length === 0) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center p-4">
        <div className="w-full max-w-md rounded-3xl border border-gray-100 bg-white p-8 text-center shadow-xl animate-in zoom-in-95 duration-300">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50 text-green-500 mb-6">
            <Brain className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-2">
            {dueCards.length === 0 && reviewedCount === 0 
              ? (mode === "all" ? t("flashcardStudy.sessionNoCardsTitle") : t("flashcardStudy.sessionNoPendingTitle")) 
              : t("flashcardStudy.sessionCompletedTitle")}
          </h2>
          <p className="text-gray-500 mb-8">
            {dueCards.length === 0 && reviewedCount === 0 
              ? (mode === "all" ? t("flashcardStudy.sessionNoCardsDesc") : t("flashcardStudy.sessionNoPendingDesc"))
              : t("flashcardStudy.sessionCompletedDesc", { count: reviewedCount })}
          </p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => fetchDueCards()}
              variant="outline"
              className="w-full h-11 border-gray-200 text-gray-700 font-bold rounded-xl"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("flashcardStudy.studyAgain")}
            </Button>
            <Button
              onClick={() => navigate("/flashcards")}
              className="w-full h-11 bg-primary hover:bg-primary/95 text-white font-bold rounded-xl shadow-md"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("flashcardStudy.backToFlashcards")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const currentCard = dueCards[currentIndex];
  const isVerbCard = currentCard && (
    currentCard.type === "verb" ||
    currentCard.metadata?.staticDeck === "verbs" ||
    currentCard.metadata?.answers ||
    currentCard.answers
  );

  const targetVerb = currentCard?.verb || currentCard?.metadata?.verb || "";
  const targetTense = currentCard?.tense || currentCard?.metadata?.tense || "";
  const targetExample = currentCard?.example || currentCard?.metadata?.example || "";
  const targetPrompts = currentCard?.metadata?.prompts || currentCard?.prompts || ["Je", "Tu", "Il/Elle/On", "Nous", "Vous", "Ils/Elles"];
  const targetAnswers = currentCard?.metadata?.answers || currentCard?.answers || {};

  const renderEEV2Back = (backText: string) => {
    if (!backText) return null;
    const hasMarkers = backText.includes("Correção:") || backText.includes("Explicação:") || backText.includes("Contexto:");
    if (!hasMarkers) {
      return <div className="whitespace-pre-line text-sm font-semibold text-slate-800 leading-relaxed">{backText}</div>;
    }
    const blocks: { type: string; title: string; content: string }[] = [];
    const lines = backText.split("\n");
    let currentBlock: { type: string; title: string; content: string[] } | null = null;
    lines.forEach(line => {
      const cleanLine = line.trim();
      if (cleanLine.startsWith("Correção:")) {
        if (currentBlock) blocks.push({ ...currentBlock, content: currentBlock.content.join("\n").trim() });
        currentBlock = { 
          type: "correction", 
          title: t("flashcardStudy.correctionTitle", { defaultValue: "Sentença Corrigida" }), 
          content: [cleanLine.replace("Correção:", "").trim()] 
        };
      } else if (cleanLine.startsWith("Explicação:")) {
         if (currentBlock) blocks.push({ ...currentBlock, content: currentBlock.content.join("\n").trim() });
         currentBlock = { 
           type: "explanation", 
           title: t("flashcardStudy.explanationTitle", { defaultValue: "Explicação do Erro" }), 
           content: [cleanLine.replace("Explicação:", "").trim()] 
         };
      } else if (cleanLine.startsWith("Contexto:")) {
         if (currentBlock) blocks.push({ ...currentBlock, content: currentBlock.content.join("\n").trim() });
         currentBlock = { 
           type: "context", 
           title: t("flashcardStudy.contextTitle", { defaultValue: "Frase de Contexto" }), 
           content: [cleanLine.replace("Contexto:", "").trim()] 
         };
      } else if (cleanLine.startsWith("Categoria:")) {
         if (currentBlock) blocks.push({ ...currentBlock, content: currentBlock.content.join("\n").trim() });
         currentBlock = { 
           type: "category", 
           title: t("flashcardStudy.categoryTitle", { defaultValue: "Categoria" }), 
           content: [cleanLine.replace("Categoria:", "").trim()] 
         };
      } else {
         if (currentBlock) {
           currentBlock.content.push(line);
         } else if (cleanLine) {
           currentBlock = { 
             type: "general", 
             title: t("flashcardStudy.infoTitle", { defaultValue: "Informação" }), 
             content: [line] 
           };
         }
      }
    });
    if (currentBlock) {
      blocks.push({ ...currentBlock, content: currentBlock.content.join("\n").trim() });
    }
    return (
      <div className="space-y-3.5 text-left w-full pt-1">
        {blocks.map((block, idx) => {
          if (!block.content) return null;
          if (block.type === "correction") {
            return (
              <div key={idx} className="space-y-1 bg-green-50 border border-green-200 p-4 rounded-xl">
                <span className="text-[10px] text-green-700 font-extrabold uppercase tracking-wider block">{block.title}</span>
                <p className="text-base font-bold text-emerald-950">{block.content}</p>
              </div>
            );
          }
          if (block.type === "explanation") {
            return (
              <div key={idx} className="space-y-1 bg-slate-50 border border-slate-100 p-4 rounded-xl">
                <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider block">{block.title}</span>
                <p className="text-xs text-slate-700 leading-relaxed font-semibold">{block.content}</p>
              </div>
            );
          }
          if (block.type === "context") {
            return (
              <div key={idx} className="space-y-1 bg-gray-50/50 border border-gray-100/50 p-4 rounded-xl">
                <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider block">{block.title}</span>
                <p className="text-xs text-slate-600 italic">"{block.content}"</p>
              </div>
            );
          }
          return (
            <div key={idx} className="space-y-1 bg-slate-50 p-4 rounded-xl border border-slate-100">
              <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider block">{block.title}</span>
              <p className="text-xs text-slate-700">{block.content}</p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 animate-in fade-in duration-500 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <Button variant="ghost" onClick={() => navigate("/flashcards")} className="text-gray-500 hover:text-gray-900 -ml-2 font-bold text-sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("flashcardStudy.exitStudy")}
        </Button>
        <h1 className="text-lg font-black text-slate-800 tracking-tight hidden sm:block">
          {typeParam === "verbs" ? t("flashcardStudy.verbsTitle") : 
           typeParam === "connectors" ? t("flashcardStudy.connectorsTitle") : 
           typeParam === "prepositions" ? t("flashcardStudy.prepositionsTitle") : 
           typeParam === "writing_v2" ? t("flashcardStudy.writingTitle") : 
           typeParam === "all" ? t("flashcardStudy.allTitle") : t("flashcardStudy.vocabTitle")}
        </h1>
        <div className="text-xs font-black text-slate-500 bg-white px-4 py-2 rounded-2xl border border-slate-100 shadow-sm">
          {currentIndex + 1} / {dueCards.length} {t("flashcardStudy.cardsRemaining")}
        </div>
      </div>

      <div className="text-xs text-purple-700 bg-purple-50/50 border border-purple-100 rounded-2xl px-6 py-3 mb-8 text-center font-medium">
        {t("flashcardStudy.srsNote")}
      </div>

      {/* Card Principal */}
      <div className="rounded-3xl bg-white p-6 shadow-sm border border-slate-100 sm:p-8">
        {!showAnswer ? (
          /* FRENTE */
          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-6 gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                {isWritingV2Flashcard(currentCard) ? (
                  <>
                    <Badge className="bg-purple-100 text-purple-800 border-none font-bold text-[9px] px-2.5 py-1 rounded-md">
                      Expression Écrite
                    </Badge>
                    {currentCard.metadata?.section && (
                      <Badge className="bg-blue-100 text-blue-800 border-none font-bold text-[9px] px-2.5 py-1 rounded-md">
                        {currentCard.metadata.section}
                      </Badge>
                    )}
                    <Badge className="bg-amber-100 text-amber-800 border-none font-bold text-[9px] px-2.5 py-1 rounded-md">
                      {currentCard.category || currentCard.metadata?.category || "Geral"}
                    </Badge>
                  </>
                ) : (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-100 uppercase tracking-wider text-[10px] font-black px-2.5 py-1">
                    {currentCard.category || "Geral"}
                  </Badge>
                )}
                {targetTense && !isWritingV2Flashcard(currentCard) && (
                  <Badge className="bg-purple-100 text-purple-800 border-none font-bold text-xs px-2.5 py-1">
                    {targetTense}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenEdit}
                  className="text-slate-400 hover:text-slate-700 h-8 rounded-xl font-bold text-xs flex items-center gap-1.5 px-3 border border-slate-100 hover:border-slate-200 hover:bg-slate-50"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Editar Card
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeleteCard}
                  className="text-rose-400 hover:text-rose-700 h-8 rounded-xl font-bold text-xs flex items-center gap-1.5 px-3 border border-rose-100 hover:border-rose-200 hover:bg-rose-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Excluir Card
                </Button>
              </div>
            </div>

            <div className="rounded-2xl bg-slate-50 p-6 mb-8">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                {t("flashcardStudy.frontHeader")}
              </p>
              <div className="text-2xl font-bold text-slate-900 leading-tight">
                {currentCard.front}
              </div>
              {targetVerb && (
                <div className="text-lg font-black text-purple-700 mt-4 inline-block bg-purple-50 px-4 py-1.5 rounded-xl border border-purple-100">
                  {targetVerb}
                </div>
              )}
            </div>

            <div className="flex flex-col w-full">
              {isVerbCard ? (
                /* CAIXAS DE CONJUGAÇÃO POR PRONOME */
                <div className="w-full">
                  <p className="text-[10px] text-slate-500 font-black uppercase tracking-wider mb-4">
                    {t("flashcardStudy.verbPrompt")}
                  </p>
                  <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                    {[
                      { key: "je", prompt: targetPrompts[0] || "Je / J'" },
                      { key: "tu", prompt: targetPrompts[1] || "Tu" },
                      { key: "ilElleOn", prompt: targetPrompts[2] || "Il / Elle / On" },
                      { key: "nous", prompt: targetPrompts[3] || "Nous" },
                      { key: "vous", prompt: targetPrompts[4] || "Vous" },
                      { key: "ilsElles", prompt: targetPrompts[5] || "Ils / Elles" }
                    ].map((item, idx) => (
                      <div key={item.key} className="grid grid-cols-1 sm:grid-cols-[150px_1fr] overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all focus-within:border-purple-400 focus-within:ring-2 focus-within:ring-purple-100">
                        <div className="flex items-center bg-slate-50 px-4 py-3 text-xs font-bold text-slate-600 sm:border-r border-slate-200 min-w-0">
                          {getPronounLabel(item.key)}
                        </div>
                        <input
                          ref={idx === 0 ? verbFirstInputRef : null}
                          type="text"
                          value={verbAnswers[item.key] || ""}
                          onChange={(e) => setVerbAnswers(prev => ({ ...prev, [item.key]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              setShowAnswer(true);
                            }
                          }}
                          placeholder={t("flashcardStudy.conjugationPlaceholder")}
                          className="w-full min-h-[48px] px-4 py-3 text-base text-slate-800 outline-none"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* RESPOSTA LIVRE ÚNICA */
                <div className="w-full">
                  <p className="text-[10px] text-slate-500 font-black uppercase tracking-wider mb-4">{t("flashcardStudy.answerPrompt")}</p>
                  <textarea
                    ref={answerInputRef}
                    value={typedAnswer}
                    onChange={(e) => setTypedAnswer(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        setShowAnswer(true);
                      }
                    }}
                    placeholder={t("flashcardStudy.answerPlaceholder")}
                    className="min-h-[160px] w-full resize-y rounded-2xl border border-slate-200 bg-white px-5 py-5 text-base text-slate-800 outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all shadow-sm"
                  />
                </div>
              )}

              <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
                <Button 
                  onClick={() => setShowAnswer(true)}
                  className="w-full sm:w-auto h-12 px-8 bg-primary hover:bg-primary/95 text-white rounded-2xl font-bold shadow-md transition-all hover:scale-[1.01]"
                >
                  {t("flashcardStudy.showAnswer")}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* VERSO (REPOSTA & COMPARAÇÃO) */
          <div className="flex flex-col">
            <div className="flex justify-between items-center mb-6 gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                {isWritingV2Flashcard(currentCard) ? (
                  <>
                    <Badge className="bg-purple-100 text-purple-800 border-none font-bold text-[9px] px-2.5 py-1 rounded-md">
                      Expression Écrite
                    </Badge>
                    {currentCard.metadata?.section && (
                      <Badge className="bg-blue-100 text-blue-800 border-none font-bold text-[9px] px-2.5 py-1 rounded-md">
                        {currentCard.metadata.section}
                      </Badge>
                    )}
                    <Badge className="bg-amber-100 text-amber-800 border-none font-bold text-[9px] px-2.5 py-1 rounded-md">
                      {currentCard.category || currentCard.metadata?.category || "Geral"}
                    </Badge>
                  </>
                ) : (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-100 uppercase tracking-wider text-[10px] font-black px-2.5 py-1">
                    {currentCard.category || "Geral"}
                  </Badge>
                )}
                {targetTense && !isWritingV2Flashcard(currentCard) && (
                  <Badge className="bg-purple-100 text-purple-800 border-none font-bold text-xs px-2.5 py-1">
                    {targetTense}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenEdit}
                  className="text-slate-400 hover:text-slate-700 h-8 rounded-xl font-bold text-xs flex items-center gap-1.5 px-3 border border-slate-100 hover:border-slate-200 hover:bg-slate-50"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Editar Card
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeleteCard}
                  className="text-rose-400 hover:text-rose-700 h-8 rounded-xl font-bold text-xs flex items-center gap-1.5 px-3 border border-rose-100 hover:border-rose-200 hover:bg-rose-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Excluir Card
                </Button>
              </div>
            </div>
            
            <div className="rounded-2xl bg-slate-50 p-6 mb-8 opacity-60">
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                {t("flashcardStudy.frontHeader")}
              </p>
              <div className="text-xl font-bold text-slate-900 line-through decoration-slate-300">
                {currentCard.front}
              </div>
            </div>

            <div className="w-full space-y-6">
              {isVerbCard ? (
                /* COMPARATIVO DETALHADO POR PRONOME */
                <div className="space-y-4">
                  <p className="text-[10px] font-black uppercase tracking-wider text-purple-700">
                    {t("flashcardStudy.answerComparison")}
                  </p>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {[
                      { key: "je", prompt: targetPrompts[0] || "Je", correct: targetAnswers.je || "" },
                      { key: "tu", prompt: targetPrompts[1] || "Tu", correct: targetAnswers.tu || "" },
                      { key: "ilElleOn", prompt: targetPrompts[2] || "Il / Elle / On", correct: targetAnswers.ilElleOn || "" },
                      { key: "nous", prompt: targetPrompts[3] || "Nous", correct: targetAnswers.nous || "" },
                      { key: "vous", prompt: targetPrompts[4] || "Vous", correct: targetAnswers.vous || "" },
                      { key: "ilsElles", prompt: targetPrompts[5] || "Ils / Elles", correct: targetAnswers.ilsElles || "" }
                    ].map(item => {
                      const userVal = (verbAnswers[item.key] || "").trim().toLowerCase();
                      const correctVal = item.correct.trim().toLowerCase();
                      const isCorrect = userVal === correctVal && correctVal !== "";

                      return (
                        <div key={item.key} className={`p-5 rounded-2xl border ${isCorrect ? "bg-emerald-50/50 border-emerald-100" : userVal ? "bg-rose-50/50 border-rose-100" : "bg-slate-50 border-slate-100"} flex flex-col space-y-3`}>
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-700 text-sm">{getPronounLabel(item.key)}</span>
                            {isCorrect ? (
                              <Badge className="bg-emerald-600 text-white text-[10px] border-none px-2 py-0.5">{t("flashcardStudy.correct")}</Badge>
                            ) : (
                              <Badge className="bg-rose-600 text-white text-[10px] border-none px-2 py-0.5">{t("flashcardStudy.incorrect")}</Badge>
                            )}
                          </div>
                          <div className="space-y-2">
                            <div className="text-xs">
                              <span className="text-slate-400 font-bold uppercase tracking-wider text-[9px] block mb-1">{t("flashcardStudy.userAnswerLabel")}</span>
                              <span className={`font-bold text-base ${isCorrect ? "text-emerald-700" : "text-rose-700"}`}>{verbAnswers[item.key] || "—"}</span>
                            </div>
                            <div className="text-xs">
                              <span className="text-purple-400 font-bold uppercase tracking-wider text-[9px] block mb-1">{t("flashcardStudy.correctAnswerLabel")}</span>
                              <span className="text-purple-800 font-bold text-base">{item.correct}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                /* COMPARATIVO LIVRE NORMAL */
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="flex flex-col bg-slate-50 rounded-2xl p-6 border border-slate-100">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 block">{t("flashcardStudy.userAnswerLabel")}</span>
                    <p className={`text-base font-medium ${typedAnswer ? "text-slate-800" : "text-slate-400 italic"} whitespace-pre-wrap leading-relaxed`}>
                      {typedAnswer || t("flashcardStudy.noAnswerTyped")}
                    </p>
                  </div>
                  
                  <div className="flex flex-col bg-emerald-50 rounded-2xl p-6 border border-emerald-100">
                    <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 mb-4 block">{t("flashcardStudy.expectedAnswerLabel")}</span>
                    {isWritingV2Flashcard(currentCard) ? (
                      renderEEV2Back(currentCard.back)
                    ) : (
                      <p className="text-lg font-bold text-emerald-900 whitespace-pre-wrap leading-relaxed">
                        {currentCard.back}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {targetExample && (
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                  <span className="font-bold text-slate-400 block text-[10px] uppercase tracking-widest mb-2">{t("flashcardStudy.usageExampleLabel")}</span>
                  <p className="text-slate-700 italic font-medium leading-relaxed">{targetExample}</p>
                </div>
              )}

              {/* CONTROLES DE REPETIÇÃO ESPAÇADA & AVALIAÇÃO */}
              <div className="pt-4">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 text-center mb-4">
                  {t("flashcardStudy.performancePrompt")}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Button 
                    disabled={saving}
                    onClick={() => handleRating("again")}
                    className="h-20 flex flex-col items-center justify-center gap-1 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 rounded-2xl transition-all shadow-sm"
                  >
                    <span className="font-black text-xs">{t("flashcardStudy.ratingAgain")}</span>
                    <span className="text-[10px] opacity-70">{t("flashcardStudy.ratingAgainTime")}</span>
                  </Button>
                  
                  <Button 
                    disabled={saving}
                    onClick={() => handleRating("hard")}
                    className="h-20 flex flex-col items-center justify-center gap-1 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-2xl transition-all shadow-sm"
                  >
                    <span className="font-black text-xs">{t("flashcardStudy.ratingHard")}</span>
                    <span className="text-[10px] opacity-70">{t("flashcardStudy.ratingHardTime")}</span>
                  </Button>
                  
                  <Button 
                    disabled={saving}
                    onClick={() => handleRating("good")}
                    className="h-20 flex flex-col items-center justify-center gap-1 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 rounded-2xl transition-all shadow-sm"
                  >
                    <span className="font-black text-xs">{t("flashcardStudy.ratingGood")}</span>
                    <span className="text-[10px] opacity-70">{t("flashcardStudy.ratingGoodTime")}</span>
                  </Button>
                  
                  <Button 
                    disabled={saving}
                    onClick={() => handleRating("easy")}
                    className="h-20 flex flex-col items-center justify-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-2xl transition-all shadow-sm"
                  >
                    <span className="font-black text-xs">{t("flashcardStudy.ratingEasy")}</span>
                    <span className="text-[10px] opacity-70">{t("flashcardStudy.ratingEasyTime")}</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-xl rounded-3xl border-none p-6 text-left shadow-2xl bg-white animate-in zoom-in-95 duration-200">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-slate-800 flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Editar Flashcard
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1">
              Modifique a frente (pergunta/termo) e o verso (resposta/explicação) do seu card.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-4">
            <div className="space-y-1.5">
              <Label htmlFor="editFront" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Frente (Pergunta ou Termo)
              </Label>
              <Textarea
                id="editFront"
                value={editFront}
                onChange={(e) => setEditFront(e.target.value)}
                rows={3}
                placeholder="Ex: Néanmoins"
                className="w-full rounded-2xl border-slate-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 text-sm font-semibold p-3.5"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="editBack" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Verso (Resposta ou Tradução)
              </Label>
              <Textarea
                id="editBack"
                value={editBack}
                onChange={(e) => setEditBack(e.target.value)}
                rows={5}
                placeholder="Ex: No entanto, contudo"
                className="w-full rounded-2xl border-slate-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 text-sm font-semibold p-3.5"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:justify-end mt-4">
            <Button
              variant="outline"
              onClick={() => setIsEditOpen(false)}
              disabled={isSavingEdit}
              className="h-11 rounded-xl border-slate-200 text-slate-600 font-bold px-5"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isSavingEdit}
              className="h-11 rounded-xl bg-primary hover:bg-primary/95 text-white font-bold px-6 shadow-md shadow-primary/20"
            >
              {isSavingEdit ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar Alterações"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
